


import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import ChartHeader from "./ChartHeader";
import ChartContainer from "./ChartContainer";
import MetricCardsContainer from "./MetricCardsContainer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ChartAssistant from "components/ChartAssistant";

// MUI platform icons
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

// DefaultLineChart configurations
import configs from "examples/Charts/LineCharts/DefaultLineChart/configs";
import colors from "assets/theme/base/colors";

import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled";

// -----------------------------------------------------------
// Plugins (unchanged)
// -----------------------------------------------------------
const renderVerticalHighlight = {
  id: "renderVerticalHighlight",
  afterDraw: (chart) => {
    try {
      if (!chart?.tooltip) return;
      const activeElements =
        chart.tooltip?.getActiveElements?.() || chart.tooltip?._active || [];
      if (!Array.isArray(activeElements) || activeElements.length === 0) return;
      const activePoint = activeElements[0];
      if (!activePoint?.element) return;

      const ctx = chart.ctx;
      const x = activePoint.element.x;
      const chartArea = chart.chartArea;
      const scaleX = chart.scales?.x;
      if (!scaleX) return;

      const tick0 = scaleX.getPixelForTick(0);
      const tick1 = scaleX.getPixelForTick(1);
      const width = tick1 && tick0 ? tick1 - tick0 : 0;

      if (x < chartArea.left || x > chartArea.right) return;
      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
      ctx.fillRect(x - width / 2, chartArea.top, width, chartArea.bottom - chartArea.top);
      ctx.restore();
    } catch (err) {
      console.warn("⚠️ renderVerticalHighlight plugin error:", err.message);
    }
  },
};

const leftToRightRevealPlugin = {
  id: "leftToRightReveal",
  beforeInit(chart, args, options) {
    chart.$revealAnimation = {
      startTime: null,
      duration: options.duration || 2000,
      running: true,
    };
    chart.resetRevealAnimation = () => {
      chart.$revealAnimation.startTime = null;
      chart.$revealAnimation.running = true;
      // Ensure chart is still attached before updating
      if (chart.ctx) {
        chart.update();
      }
    };
  },
  beforeDatasetsDraw(chart) {
    const chartArea = chart.chartArea;
    const ctx = chart.ctx;
    if (!chartArea || !ctx) return;
    const animationState = chart.$revealAnimation;
    if (!animationState.startTime) {
      animationState.startTime = performance.now();
    }
    const now = performance.now();
    const elapsed = now - animationState.startTime;
    const progress = Math.min(elapsed / animationState.duration, 1);
    const clipWidth = chartArea.left + (chartArea.right - chartArea.left) * progress;
    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, chartArea.top, clipWidth - chartArea.left, chartArea.bottom - chartArea.top);
    ctx.clip();
    if (progress < 1 && animationState.running !== false) {
      animationState.running = true;
      requestAnimationFrame(() => {
        // 🛡️ Safety Warning: Ensure chart is actively attached before updating to prevent "ownerDocument" errors
        if (chart.ctx && chart.canvas && chart.canvas.isConnected) {
          chart.update();
        }
      });
    } else {
      animationState.running = false;
    }
  },
  afterDatasetsDraw(chart) {
    if (chart.ctx) {
      chart.ctx.restore();
    }
  },
};

// -----------------------------------------------------------
// Helper to render platform icon
// -----------------------------------------------------------
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case "facebook":
      return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "youtube":
      return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "instagram":
      return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "twitter":
      return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    default:
      return null;
  }
};

// -----------------------------------------------------------
// Main component
// -----------------------------------------------------------
// 🚀 NEW IMPORTS
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import dayjs from "dayjs";

// Helper for Aggregation
const aggregateData = (labels, datasets, mode, isRate, isSnapshot) => {
  if (mode !== "monthly" || !labels || !datasets) return { labels, datasets };

  const buckets = {}; // "Jan-2024": { label: "Jan 2024", indices: [0, 1, 2...] }
  const bucketOrder = [];

  // 1. Create Buckets
  labels.forEach((dateStr, index) => {
    // Assuming dateStr is parseable by dayjs, e.g., YYYY-MM-DD
    const d = dayjs(dateStr);
    if (!d.isValid()) return;
    const key = d.format("MMM YYYY"); // "Jan 2025"

    if (!buckets[key]) {
      buckets[key] = { label: key, indices: [] };
      bucketOrder.push(key);
    }
    buckets[key].indices.push(index);
  });

  // 2. Aggregate Labels
  const newLabels = bucketOrder;

  // 3. Aggregate Datasets
  const newDatasets = datasets.map((ds) => {
    const originalFlags = ds.isPredictedFlags || [];
    const newFlags = [];

    const newData = bucketOrder.map((key) => {
      const indices = buckets[key].indices;
      const values = indices.map((i) => ds.data[i]).filter((v) => v !== null && v !== undefined);

      // Aggregate Flags
      // Get the flags for the current bucket indices
      // 🚀 UNIFIED LOGIC: Always check the LAST value of the month.
      // If the month ends with a predicted value, the whole month is considered predicted.
      const lastIndex = indices[indices.length - 1];
      const aggregatedFlag = originalFlags[lastIndex] || false;

      newFlags.push(aggregatedFlag);

      if (values.length === 0) return 0;

      // 📷 SNAPSHOT LOGIC: Use the Last Value for the month
      if (isSnapshot) {
        return values[values.length - 1];
      }

      const sum = values.reduce((a, b) => a + b, 0);

      if (isRate) {
        // Average for Rates
        return sum / values.length;
      } else {
        // Sum for Counts
        return sum;
      }
    });

    return { ...ds, data: newData, isPredictedFlags: newFlags };
  });

  return { labels: newLabels, datasets: newDatasets };
};

// -----------------------------------------------------------
// Main component
// -----------------------------------------------------------
function DefaultLineChart({
  title = "",
  description = "",
  height = "19.125rem",
  chart,
  aggregate = false,
  rate = false,
  isSnapshot, // 🚀 New Prop
  is_derived,
  plugins = [],
  yAxisTicks = null,
  backgroundColorPluginOptions = {},
  onPointClick = () => { },
  isEntitled = true,
  featureName = "",
  planRequired = "",
  isEmpty = false,
  metrics = null,
  rawData,
  entitlements, // ✅ New ENTITLEMENTS Prop
  disableToggle = false,
  viewMode: controlledViewMode, // ✅ Receive controlled state
  onViewModeChange, // ✅ Receive handler
  profileColorMap, // ✅ Global Color Map
  overrideOptions, // ✅ Accept Overrides
  customFooter, // ✅ NEW PROP
}) {
  const chartContainerRef = useRef(null);

  // 🚀 STATE FOR TOGGLE (Local fallback if not controlled)
  const [localViewMode, setLocalViewMode] = React.useState("daily");

  // Use controlled state if available, else local
  const viewMode = controlledViewMode || localViewMode;

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      if (onViewModeChange) {
        onViewModeChange(newMode);
      } else {
        setLocalViewMode(newMode);
      }
    }
  };

  useEffect(() => {
    // Reset animation on data change (handled by existing logic below if chart props change)
    if (chartContainerRef.current?.resetRevealAnimation) {
      chartContainerRef.current.resetRevealAnimation();
    }

    // ⬇️=========================================
    // ⬇️ ADDED: Logic to reset cursor on mouse leave
    // ⬇️=========================================
    const chartInstance = chartContainerRef.current?.chart;
    if (chartInstance) {
      const canvas = chartInstance.canvas;
      const handleMouseLeave = () => {
        canvas.style.cursor = 'default';
      };
      canvas.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        if (canvas) {
          canvas.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }
  }, [chart, viewMode]); // Rerun when chart OR viewMode changes

  const chartDatasets = chart?.datasets
    ? chart.datasets.map((dataset) => ({
      ...dataset,
      fill: false,
      tension: 0.4,
      pointRadius: 5,
      pointHoverRadius: 7,
      borderWidth: 3,
      // 🚀 FIX: Allow undefined colors to flow through to 'configs' logic
      borderColor: dataset.borderColor || colors[dataset.color]?.main,
      backgroundColor: dataset.backgroundColor || colors[dataset.color]?.main,
      segment: {
        borderDash: (ctx) => {
          try {
            // 🚀 FIX: Get the ACTUAL dataset being rendered (aggregated one), not the closure variable
            const currentDataset = ctx.chart.data.datasets[ctx.datasetIndex];
            const flags = currentDataset?.isPredictedFlags || [];

            const i = ctx.p0DataIndex;
            // Only dotted if BOTH start and end points are predicted
            return (flags[i] && flags[i + 1]) ? [5, 5] : [];
          } catch (e) {
            return [];
          }
        },
      },
    }))
    : [];

  const previousChartDatasets = chart?.previousdatasets
    ? chart.previousdatasets.map((dataset) => ({ ...dataset }))
    : [];

  const profileMetrics =
    chart?.datasets?.map((dataset, index) => {
      // 🚀 Feature: Allow skipping metric card generation for auxiliary datasets
      if (dataset.skipMetricCard) return null;

      const previousDataset = chart.previousdatasets?.[index] || null;
      let currentMetricValue = dataset.data?.[dataset.data.length - 1] || 0;
      let previousMetricValue = previousDataset?.data?.[previousDataset.data.length - 1] || 0;

      if (aggregate) {
        const currentSum = dataset?.data?.reduce((acc, val) => acc + val, 0) || 0;
        const previousSum = previousDataset?.data?.reduce((acc, val) => acc + val, 0) || 0;
        currentMetricValue = currentSum;
        previousMetricValue = previousSum;
      }

      if (rate) {
        const currentValid = dataset?.data?.filter((v) => v != null) || [];
        const previousValid = previousDataset?.data?.filter((v) => v != null) || [];
        const currentSum = currentValid.reduce((a, v) => a + v, 0);
        const previousSum = previousValid.reduce((a, v) => a + v, 0);
        currentMetricValue = currentValid.length ? currentSum / currentValid.length : 0;
        previousMetricValue = previousValid.length ? previousSum / previousValid.length : 0;
      }

      // Note: We might want to apply snapshot logic to Metric Cards too if needed, 
      // but usually the metric card shows the LATEST value anyway (lines 201-202).
      // So no change needed here for profileMetrics unless 'aggregate' prop was wrongly set.

      if (metrics && metrics[index]) {
        currentMetricValue = metrics[index]?.currentMetricValue ?? currentMetricValue;
        previousMetricValue = metrics[index]?.previousMetricValue ?? previousMetricValue;
        // Allow overriding metric name if provided
        if (metrics[index]?.metricName) {
          dataset.metricName = metrics[index].metricName;
        }
      }

      return {
        profilePic: dataset.profilePictureUrl || "https://via.placeholder.com/150",
        name: dataset.label || "Unknown Profile",
        platform: dataset.platform || "Unknown Platform",
        metricName: dataset.metricName || "Metric",
        // If it's a string (like "N/A"), use it directly, otherwise locale string
        metricValue: typeof currentMetricValue === 'string' ? currentMetricValue : currentMetricValue.toLocaleString(),
        previousMetricValue: typeof previousMetricValue === 'string' ? previousMetricValue : previousMetricValue.toLocaleString(),
      };
    }).filter(m => m !== null) || [];

  // 🚀 APPLY AGGREGATION
  const isRateChart = rate; // Prop 'rate' determines if it's Avg or Sum
  const { labels: finalLabels, datasets: finalDatasets } = aggregateData(
    chart?.labels || [],
    chartDatasets,
    viewMode,
    isRateChart,
    isSnapshot // ✅ Pass snapshot prop
  );

  // 🚀 Determine Y-Axis Title from datasets
  const yAxisTitle = chartDatasets?.[0]?.metricName || "";

  const { data, options } = configs(finalLabels, finalDatasets, yAxisTicks, profileColorMap, yAxisTitle);

  // 🚀 OVERRIDE X-AXIS TICKS FOR MONTHLY VIEW
  if (viewMode === "monthly") {
    // Ensure we exist safely
    if (options.scales && options.scales.x && options.scales.x.ticks) {
      options.scales.x.ticks.callback = function (value, index) {
        // value is the index, get label from data
        const label = this.getLabelForValue(value);
        // If it's effectively "MMM YYYY", just return it.
        // Or if chart.js parses it weirdly, we can rely on our finalLabels array (bucketOrder)
        // Since we passed "Jan 2025" string, getLabelForValue should return it (or date object).
        // Let's force return the string we created.
        return finalLabels[index];
      };
    }
  }

  // ✅ Enhanced click handling (same principle as VerticalBarChart)
  const handleClick = (event, elements, chartInstance) => {
    if (!onPointClick || !chartInstance) return;

    const clickedElements = chartInstance.getElementsAtEventForMode(
      event,
      "index",
      { intersect: false },
      true
    );

    // Case 1: Found a point or vertical position near a data point
    if (clickedElements.length > 0) {
      const index = clickedElements[0].index;
      const date = chart.labels[index];
      if (date) onPointClick(date);
      return;
    }

    // Case 2: Fallback – click between points but within chart area
    const xScale = chartInstance.scales.x;
    const chartArea = chartInstance.chartArea;
    if (event.x >= chartArea.left && event.x <= chartArea.right) {
      const nearestIndex = Math.round(xScale.getValueForPixel(event.x));
      const date = chart.labels[nearestIndex];
      if (date) onPointClick(date);
    }
  };

  // ✅ Deep Merge Helper (Simple version)
  const deepMerge = (target, source) => {
    for (const key in source) {
      if (source[key] instanceof Object && key in target) {
        Object.assign(source[key], deepMerge(target[key], source[key]));
      }
    }
    Object.assign(target || {}, source);
    return target;
  };

  let mergedOptions = {
    ...options,
    plugins: {
      ...options.plugins,
      legend: { display: false },
      leftToRightReveal: { duration: 1500 },
      backgroundColorPlugin: backgroundColorPluginOptions,
    },
    onClick: handleClick,
    onHover: (event, chartElements, chart) => {
      if (onPointClick) {
        chart.canvas.style.cursor = 'pointer';
      }
    },
  };

  // ✅ Apply Overrides if provided (Deep Merge for plugins)
  if (overrideOptions) {
    mergedOptions = deepMerge(mergedOptions, overrideOptions);
  }

  const allPlugins = [renderVerticalHighlight, leftToRightRevealPlugin, ...plugins];
  const hasData = !isEmpty && chart?.labels?.length > 0 && chartDatasets?.length > 0;

  // -----------------------------------------------------------
  // Render
  // -----------------------------------------------------------
  return (
    <Card>

      <ChartHeader
        title={title}
        description={description}
        profilePictures={profileMetrics.map((m) => m.profilePic)}
        is_derived={is_derived}
      >
        {/* Daily/Monthly Toggle - Custom Teal Style (Conditionally Hidden) */}
        {!disableToggle && (
          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #dcdcdc",
              borderRadius: "50px", // Pill shape container
              padding: "4px",
              mr: 2,
              height: 40,
              backgroundColor: "#ffffff", // ✅ White Background
            }}
          >
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              sx={{
                height: "100%",
                gap: "4px", // Space between buttons
                ".MuiToggleButtonGroup-grouped": {
                  margin: 0,
                  border: 0,
                  borderRadius: "50px !important", // Force pill shape for buttons
                  "&:not(:first-of-type)": {
                    borderRadius: "50px !important",
                    borderLeft: "0",
                    marginLeft: 0,
                  },
                  "&:first-of-type": {
                    borderRadius: "50px !important",
                  },
                },
              }}
            >
              <ToggleButton
                value="daily"
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: "0.85rem",
                  fontWeight: "medium",
                  textTransform: "none",
                  color: "#4a4a4a",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#4d9b92", // Comparedly Teal
                    color: "#ffffff",
                    boxShadow: "0 2px 6px rgba(77, 155, 146, 0.4)",
                    "&:hover": {
                      backgroundColor: "#3d8b82",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(77, 155, 146, 0.1)",
                  }
                }}
              >
                Daily
              </ToggleButton>
              <ToggleButton
                value="monthly"
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: "0.85rem",
                  fontWeight: "medium",
                  textTransform: "none",
                  color: "#4a4a4a",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#4d9b92", // Comparedly Teal
                    color: "#ffffff",
                    boxShadow: "0 2px 6px rgba(77, 155, 146, 0.4)",
                    "&:hover": {
                      backgroundColor: "#3d8b82", // Darker teal hover
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(77, 155, 146, 0.1)",
                  }
                }}
              >
                Monthly
              </ToggleButton>
            </ToggleButtonGroup>
          </MDBox>
        )}

        {/* ✅ AI Chart Assistant moved to Header */}
        <ChartAssistant
          chartData={chart}
          title={title}
          context={description}
          rawData={rawData}
          sx={{
            position: "static",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", // Keep subtle shadow
            ml: 2
          }}
          entitlements={entitlements} // ✅ Pass entitlements
        />
      </ChartHeader>


      {/* WRAPPER FOR CONTENT BELOW HEADER */}
      <div style={{ position: "relative" }}>

        {/* 🚫 Frosted glass entitlement overlay */}
        {!isEntitled && (
          <ChartNotEntitled
            featureName={featureName}
            planRequired={planRequired}
          />
        )}



        <MetricCardsContainer metrics={profileMetrics} />

        {/* ✅ Legend */}
        {data?.datasets?.length > 0 && (
          <MDBox display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" px={2} py={1}>
            {data.datasets.map((ds, i) => {
              if (ds.skipLegend) return null; // ✅ Support skipping legend items

              return (
                <MDBox key={i} display="flex" alignItems="center" mr={2} mb={1}>
                  <MDBox
                    sx={{
                      width: 20,
                      height: 10,
                      borderRadius: "2px",
                      backgroundColor: ds.borderColor || colors.dark.main,
                      mr: 1,
                    }}
                  />
                  {getPlatformIcon(ds.platform)}
                  <MDTypography variant="button" color="text" sx={{ fontWeight: "regular", ml: 0.5 }}>
                    {ds.label}
                  </MDTypography>
                </MDBox>
              );
            })}
          </MDBox>
        )}

        {hasData ? (
          <ChartContainer
            key={`${chart.labels.length}-${chart.datasets.length}`}
            ref={chartContainerRef}
            height={height}
            data={data}
            options={mergedOptions}
            plugins={allPlugins}
          // ✅ This style prop is not needed, as options.onHover handles it
          // style={{ cursor: "pointer" }} 
          />
        ) : (
          <MDBox sx={{ p: "10px" }}>
            <MDTypography variant="subtitle1" color="text" align="center">
              No content found in the selected date range.
            </MDTypography>
          </MDBox>
        )}

        {customFooter}
      </div>
      {/* Disclaimer Footer - moved from header */}
      {is_derived && (
        <MDBox p={1} display="flex" justifyContent="flex-end">
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontStyle: "italic",
              fontSize: "0.7rem",
              opacity: 0.7,
              mr: 1
            }}
          >
            *Metric calculated by Comparedly
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

// -----------------------------------------------------------
// PropTypes & Defaults
// -----------------------------------------------------------
DefaultLineChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aggregate: PropTypes.bool,
  rate: PropTypes.bool,
  chart: PropTypes.shape({
    labels: PropTypes.array.isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        profilePictureUrl: PropTypes.string,
      })
    ).isRequired,
    previousdatasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        profilePictureUrl: PropTypes.string,
      })
    ),
  }).isRequired,
  plugins: PropTypes.array,
  yAxisTicks: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  backgroundColorPluginOptions: PropTypes.object,
  onPointClick: PropTypes.func, // ✅ added click handler prop
  isEmpty: PropTypes.bool,
  metrics: PropTypes.array,
  rawData: PropTypes.any, // ✅ Can be anything (object, array)
  entitlements: PropTypes.object, // ✅ Added propType
  profileColorMap: PropTypes.object, // ✅ Global color map
  overrideOptions: PropTypes.object, // ✅ Helper for custom overrides
};

export default DefaultLineChart;
